import { experienceEvent } from '@/events/builders'
import type { EventTuple } from '@/events/types'
import type { PageEventHandler } from '@/play/pageTypes'
import { isInBoundsXYZ, parseKeyX, parseKeyY, parseKeyZ, posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'

/**
 * Things: a group of cells with an id that moves as one (an ABC block, later a word tile). The
 * cells are the only truth: a thing's id is read from its cells by the page's `tagOf` (Letter
 * blocks: every block has colours of its own, one a hair off the next, so the tag is the colour),
 * and a thing is the face-connected cells with one tag. So identity rides along with the cells
 * through every event, undo, redo and fresh start, and nothing else has to be kept in step. What a
 * thing means (its letter) is page data keyed by the tag; the engine never knows.
 *
 * Pure (no store, React or UI): the reducer imports the move event through a page's events.ts.
 */

export type ThingTag = number | string
/** The thing a voxel belongs to, or null for the world (floor, table, anything loose). */
export type TagOf = (voxel: VoxelData) => ThingTag | null

export type Thing = {
  tag: ThingTag
  /** The thing's cells, in document keys. */
  cells: Array<[key: number, voxel: VoxelData]>
  keys: number[]
}

type Readable = Pick<VoxelMap, 'get'>
type Iterable_ = { forEach(fn: (value: VoxelData, key: number) => void): void }

const NEIGHBOURS: ReadonlyArray<readonly [number, number, number]> = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]]

/** The thing the cell `key` is part of (a flood fill over face neighbours of the same tag), or null. */
export function thingAt(base: Readable, key: number, tagOf: TagOf, bounds: Position): Thing | null {
  const first = base.get(key)
  if (!first) return null
  const tag = tagOf(first)
  if (tag === null) return null
  const seen = new Set<number>([key])
  const cells: Array<[number, VoxelData]> = []
  const stack = [key]
  while (stack.length > 0) {
    const k = stack.pop()!
    const v = base.get(k)!
    cells.push([k, v])
    const x = parseKeyX(k), y = parseKeyY(k), z = parseKeyZ(k)
    for (const [dx, dy, dz] of NEIGHBOURS) {
      const nx = x + dx, ny = y + dy, nz = z + dz
      if (!isInBoundsXYZ(nx, ny, nz, bounds)) continue
      const nk = posKeyXYZ(nx, ny, nz)
      if (seen.has(nk)) continue
      const nv = base.get(nk)
      if (!nv || tagOf(nv) !== tag) continue
      seen.add(nk)
      stack.push(nk)
    }
  }
  return { tag, cells, keys: cells.map(([k]) => k) }
}

/** Every thing in the world by tag (one scan; for reading what is where, e.g. a shelf). */
export function thingsIn(base: Iterable_, tagOf: TagOf): Map<ThingTag, Thing> {
  const out = new Map<ThingTag, Thing>()
  base.forEach((v, key) => {
    const tag = tagOf(v)
    if (tag === null) return
    let thing = out.get(tag)
    if (!thing) out.set(tag, (thing = { tag, cells: [], keys: [] }))
    thing.cells.push([key, v])
    thing.keys.push(key)
  })
  return out
}

/** The box a set of cells fills (inclusive). */
export function cellsBox(keys: readonly number[]): { min: Position; max: Position } {
  const min = { x: Infinity, y: Infinity, z: Infinity }
  const max = { x: -Infinity, y: -Infinity, z: -Infinity }
  for (const k of keys) {
    const x = parseKeyX(k), y = parseKeyY(k), z = parseKeyZ(k)
    if (x < min.x) min.x = x; if (y < min.y) min.y = y; if (z < min.z) min.z = z
    if (x > max.x) max.x = x; if (y > max.y) max.y = y; if (z > max.z) max.z = z
  }
  return { min, max }
}

/**
 * Where a thing moved sideways by (dx, dz) comes to rest when let go from above: the vertical move
 * dy that puts it straight on top of whatever is under its footprint (the floor, the table, another
 * thing: stacking). It is rigid gravity's rule (src/play/physics/rigidGravity.ts) with the fall done
 * at once: a body falls as a unit until a cell directly under one of its cells is taken. Measured
 * from the top of the world down, so it never lands inside an overhang. `occupied` is the world
 * without the thing itself. Null when it does not fit under the top of the world.
 */
export function landingDy(
  occupied: (x: number, y: number, z: number) => boolean,
  keys: readonly number[],
  dx: number,
  dz: number,
  bounds: Position,
): number | null {
  // The lowest cell of the thing in each of its columns.
  const lowest = new Map<number, { x: number; z: number; y: number }>()
  let maxY = -Infinity
  for (const k of keys) {
    const x = parseKeyX(k) + dx, y = parseKeyY(k), z = parseKeyZ(k) + dz
    const col = x * 4096 + z
    const c = lowest.get(col)
    if (!c) lowest.set(col, { x, z, y })
    else if (y < c.y) c.y = y
    if (y > maxY) maxY = y
  }
  let dy = -Infinity
  for (const { x, z, y } of lowest.values()) {
    let top = 0
    for (let yy = bounds.y - 1; yy >= 0; yy--) {
      if (occupied(x, yy, z)) { top = yy + 1; break }
    }
    dy = Math.max(dy, top - y)
  }
  if (!Number.isFinite(dy) || maxY + dy >= bounds.y) return null
  return dy
}

/** Moves cells by a whole-cell offset in place: all leave, then all arrive (cells out of bounds are lost). */
export function moveCells(base: VoxelMap, keys: readonly number[], by: Position, bounds: Position): void {
  const moving: Array<[number, VoxelData]> = []
  for (const k of keys) {
    const v = base.get(k)
    if (v) moving.push([k, v])
  }
  for (const [k] of moving) base.delete(k)
  for (const [k, v] of moving) {
    const x = parseKeyX(k) + by.x, y = parseKeyY(k) + by.y, z = parseKeyZ(k) + by.z
    if (isInBoundsXYZ(x, y, z, bounds)) base.set(posKeyXYZ(x, y, z), v)
  }
}

export type ThingMove = { keys: number[]; by: Position }

/**
 * A page's durable move event (`experience.<page>.<verb>`): a thing's cells moved by a whole-cell
 * offset, one event per drop, so one drop is one undo step. The cells carry the thing's identity,
 * so the event needs nothing else. Export `handlers` from the page's events.ts.
 */
export function thingMoveEvent(name: string) {
  const tag = name as `experience.${string}`
  return {
    name,
    event: (keys: readonly number[], by: Position): EventTuple => experienceEvent(tag, { keys: [...keys], by: { x: by.x, y: by.y, z: by.z } } satisfies ThingMove),
    handlers: {
      [name]: (ctx, event) => {
        const move = event[1] as Partial<ThingMove> | undefined
        if (!move || !Array.isArray(move.keys) || !move.by) return
        const { x, y, z } = move.by
        if (![x, y, z].every((n) => typeof n === 'number' && Number.isInteger(n))) return
        moveCells(ctx.base, move.keys, move.by, ctx.meta.bounds)
      },
    } as Record<string, PageEventHandler>,
  }
}

export type ThingMoveEvent = ReturnType<typeof thingMoveEvent>
